// Чистая логика раздела «Финансы» (обзор, баланс, расходы, счета партнёров).
// Деньги приходят строками «123.45» (Go moneyString) и складываются здесь в
// копейках (BigInt). Number — только для сравнений, ширины шкал и высоты
// столбиков графика, никогда для показа суммы.

import { CAMPAIGN_STATUS_GROUPS } from "./campaignList";
import type { StatusTone } from "../design/StatusTag";
import type { BillingOp, MarketingCampaign } from "./types";

export interface FinanceAccount {
  id: string;
  companyId: string;
  kind?: string;
  name: string;
  balance: string;
  reserved: string;
  buffer?: string;
  spendable?: string;
  /** Архивный партнёр (удалён в CRM): в итоги сети не входит — как в Go totals и «Счетах партнёров». */
  archived?: boolean;
  /**
   * Списано с этого счёта за 30 дней по цене плательщика (₽ строкой, Go:
   * billing_ops charge + уточнения по сообщениям). Одинаково для УК и партнёра,
   * без себестоимости. undefined — старый сервер.
   */
  spent30d?: string;
  /** Платных сообщений, списанных с этого счёта за 30 дней (Go messages30d). */
  messages30d?: number;
  /**
   * Действующих кампаний (работают или на паузе), которые платят с этого счёта.
   * 0 — счёт заведён, но SMS с него не шлют: напоминать о пополнении незачем.
   * undefined — старый сервер (считаем, что кампании есть, как раньше).
   */
  payingCampaigns?: number;
  /** Последнее зачисление на счёт: ISO-время и сумма ₽ строкой; null — не было. */
  lastTopupAt?: string | null;
  lastTopupAmount?: string | null;
}

/** Итоги по всем счетам партнёров — GET /api/billing → totals (только УК; партнёру null). */
export interface BillingTotals {
  balance: string;
  spendable: string;
  reserved: string;
  /** Сумма запасов всех счетов (запас × число счетов). */
  buffer: string;
  accounts: number;
  /**
   * Счетов с действующими кампаниями, где доступно 0 ₽ или баланс меньше
   * двойного запаса — те же, что колокольчик УК перечисляет в «Партнёрам пора
   * пополнить счёт» (needsTopup).
   */
  lowCount: number;
  /** Из них — где отправка уже остановлена (доступно 0 ₽); нет у старого Go. */
  stoppedCount?: number;
}

export interface BillingResponse {
  accounts?: FinanceAccount[];
  account?: FinanceAccount;
  totals?: BillingTotals | null;
}

/** Расход за день МСК — GET /api/spending → byDay. */
export interface SpendDay {
  date: string;
  spent: string;
  messages: number;
}

/** Расход по каналу — GET /api/spending → byChannel. */
export interface SpendChannel {
  channel: string;
  spent: string;
  messages: number;
}

/** Строка ленты операций: у ленты УК по всем счетам есть счёт и юрлицо. */
export interface FinanceOp extends BillingOp {
  accountId?: string;
  companyName?: string;
}

const MONEY_RE = /^(-?)(\d+)(?:\.(\d{1,2}))?$/;

/** «123.45» → 12345n; не число (или нет значения) → null. */
export function toCents(value: string | null | undefined): bigint | null {
  const match = MONEY_RE.exec((value ?? "").trim());
  if (!match) return null;
  const cents =
    BigInt(match[2]) * 100n + BigInt((match[3] ?? "").padEnd(2, "0"));
  return match[1] ? -cents : cents;
}

/** 12345n → «123.45» (как moneyString в Go). */
export function centsToMoney(cents: bigint): string {
  const negative = cents < 0n;
  const abs = negative ? -cents : cents;
  return (
    (negative ? "-" : "") +
    abs / 100n +
    "." +
    String(abs % 100n).padStart(2, "0")
  );
}

/** Суммы считаем в копейках, без накопления ошибки двоичной дроби. Отсутствие значения не равно нулю. */
export function sumMoney(
  values: Array<string | undefined>,
): string | undefined {
  let total = 0n;
  for (const value of values) {
    const cents = toCents(value);
    if (cents === null) return undefined;
    total += cents;
  }
  return centsToMoney(total);
}

/** Счёт партнёра (не учётный счёт УК). */
export function isPartnerAccount(account: {
  kind?: string;
  companyId: string;
}): boolean {
  return account.kind !== "network" && !!account.companyId;
}

/** Счёт действующего партнёра — тот же набор, что Go totals и «Счета партнёров» (WHERE NOT p.archived). */
export function isActivePartnerAccount(account: {
  kind?: string;
  companyId: string;
  archived?: boolean;
}): boolean {
  return isPartnerAccount(account) && !account.archived;
}

export function accountTotals(source: FinanceAccount[]) {
  // Счёт УК — счётчик расходов, его отрицательный остаток не уменьшает деньги партнёров.
  // Архивных партнёров не считаем, чтобы итоги совпадали со «Счетами партнёров».
  const accounts = source.filter(isActivePartnerAccount);
  return {
    balance: sumMoney(accounts.map((a) => a.balance)),
    reserved: sumMoney(accounts.map((a) => a.reserved)),
    buffer: sumMoney(accounts.map((a) => a.buffer)),
    spendable: sumMoney(accounts.map((a) => a.spendable)),
  };
}

/**
 * Баланс меньше двойного запаса — пора напомнить о пополнении (порог прототипа,
 * тот же, что `totals.lowCount` в GET /api/billing — Go billingBelowDoubleBuffer).
 * Не путать с `totals.lowBalance` в /api/marketing/clients: там «доступно ≤ 0»,
 * отправка уже остановлена. Без запаса или баланса — false: не выдумываем.
 */
export function belowDoubleBuffer(account: {
  balance: string;
  buffer?: string | null;
}): boolean {
  const balance = toCents(account.balance);
  const buffer = toCents(account.buffer ?? undefined);
  if (balance === null || buffer === null || buffer <= 0n) return false;
  return balance < buffer * 2n;
}

/** Доступного к отправке нет: платные сообщения не уйдут. */
export function nothingSpendable(spendable: string | undefined): boolean {
  const cents = toCents(spendable);
  return cents !== null && cents <= 0n;
}

/**
 * Тревога по счёту, как Go accountAlertState: «empty» — доступно 0 ₽, отправка
 * стоит; «low» — баланс меньше двойного запаса; «» — всё в порядке.
 */
export function alertState(account: {
  balance: string;
  buffer?: string | null;
  spendable?: string;
}): "empty" | "low" | "" {
  if (nothingSpendable(account.spendable)) return "empty";
  if (belowDoubleBuffer(account)) return "low";
  return "";
}

/** С этого счёта платят действующие кампании (у старого сервера признака нет — считаем, что да). */
export function hasPayingCampaigns(account: {
  payingCampaigns?: number;
}): boolean {
  return account.payingCampaigns === undefined || account.payingCampaigns > 0;
}

/**
 * Пора напомнить о пополнении: действующие кампании платят с этого счёта, а
 * доступно 0 ₽ или баланс меньше двойного запаса. Один признак для плитки
 * «Партнёров ниже запаса ×2», чипа «Счетов партнёров» и колокольчика УК (Go
 * billingTotals.lowCount и notificationAccountsSQL) — числа совпадают.
 */
export function needsTopup(account: {
  balance: string;
  buffer?: string | null;
  spendable?: string;
  payingCampaigns?: number;
}): boolean {
  return hasPayingCampaigns(account) && alertState(account) !== "";
}

export interface FinanceSummary {
  balance?: string;
  spendable?: string;
  reserved?: string;
  buffer?: string;
  accounts: number;
  /** Пора напомнить о пополнении (needsTopup). */
  low: number;
  /** Из них — отправка уже остановлена; undefined — старый сервер не прислал. */
  stopped?: number;
  /** server — итог Go (вся доступная сеть); browser — посчитан по отфильтрованным счетам. */
  source: "server" | "browser";
}

/**
 * Итог блока баланса. Без фильтров у УК берём `totals` сервера; с фильтром
 * по юрлицу/городу (или если Go ещё не прислал totals) — считаем по видимым
 * счетам партнёров в копейках.
 */
export function financeSummary(
  accounts: FinanceAccount[],
  totals: BillingTotals | null | undefined,
  filtered: boolean,
): FinanceSummary {
  if (totals && !filtered)
    return {
      balance: totals.balance,
      spendable: totals.spendable,
      reserved: totals.reserved,
      buffer: totals.buffer,
      accounts: totals.accounts,
      low: totals.lowCount,
      stopped: totals.stoppedCount,
      source: "server",
    };
  const partner = accounts.filter(isActivePartnerAccount);
  const due = partner.filter(needsTopup);
  return {
    ...accountTotals(partner),
    accounts: partner.length,
    low: due.length,
    stopped: due.filter((a) => alertState(a) === "empty").length,
    source: "browser",
  };
}

export type AccountState = "network" | "idle" | "empty" | "low" | "ok";

export const ACCOUNT_STATE: Record<
  AccountState,
  { label: string; tone: StatusTone; hint: string }
> = {
  network: {
    label: "Учётный счёт УК",
    tone: "neutral",
    hint: "Счётчик расходов УК: отправку ограничивает баланс SMS-центра",
  },
  idle: {
    label: "Без кампаний",
    tone: "neutral",
    hint: "Баланс мал, но действующих кампаний за счёт этого юрлица нет — пополнение не срочно",
  },
  empty: {
    label: "Пополнить",
    tone: "error",
    hint: "Доступно 0 ₽ — платные сообщения не уйдут",
  },
  low: {
    label: "Мало",
    tone: "warning",
    hint: "Баланс меньше двойного запаса — скоро остановится отправка",
  },
  ok: { label: "Норма", tone: "success", hint: "Баланса хватает" },
};

export function accountState(account: FinanceAccount): AccountState {
  if (!isPartnerAccount(account)) return "network";
  const alert = alertState(account);
  if (!alert) return "ok";
  // Мало денег, но с этого счёта ничего не отправляется — не тревога.
  return hasPayingCampaigns(account) ? alert : "idle";
}

/** «01.10.2026 · 5 000,00 ₽» — последнее пополнение счёта; не было — «—». */
export function lastTopupText(
  account: { lastTopupAt?: string | null; lastTopupAmount?: string | null },
  format: (value: string) => string,
): string {
  if (!account.lastTopupAt) return "—";
  const date = new Date(account.lastTopupAt);
  if (!Number.isFinite(date.getTime())) return "—";
  const day = date.toLocaleDateString("ru-RU", {
    timeZone: "Europe/Moscow",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  return account.lastTopupAmount ? `${day} · ${format(account.lastTopupAmount)}` : day;
}

/** Города юрлица по справочнику городов: до трёх названиями, дальше «и ещё N». */
export function companyCities(
  tenants: Array<{ city: string; partnerIds: string[] }>,
  companyId: string,
): string {
  if (!companyId) return "—";
  const cities = [
    ...new Set(
      tenants
        .filter((t) => t.partnerIds?.includes(companyId))
        .map((t) => t.city)
        .filter(Boolean),
    ),
  ];
  if (!cities.length) return "—";
  return cities.length <= 3
    ? cities.join(", ")
    : `${cities.slice(0, 3).join(", ")} и ещё ${cities.length - 3}`;
}

/** Доля частей баланса для полосы «доступно / резерв / запас», % (только ширина). */
export function balanceSplit(account: {
  balance: string;
  reserved: string;
  buffer?: string;
  spendable?: string;
}): { spendable: number; reserved: number; buffer: number } {
  const num = (value: string | undefined) => {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : 0;
  };
  const balance = num(account.balance);
  const spendable = num(account.spendable);
  const reserved = num(account.reserved);
  const buffer = Math.min(num(account.buffer), balance);
  const whole = Math.max(balance, spendable + reserved + buffer, 1);
  const pct = (value: number) =>
    Math.max(0, Math.min(100, Math.round((value / whole) * 1000) / 10));
  return {
    spendable: pct(spendable),
    reserved: pct(reserved),
    buffer: pct(buffer),
  };
}

/** Точка графика расходов (совместима с TrendPoint дизайн-системы). */
export interface SpendPoint {
  label: string;
  value: number;
  partial?: boolean;
  date: string;
  spent: string;
  messages: number;
}

function shift(day: string, days: number): string {
  const value = new Date(`${day}T12:00:00Z`);
  value.setUTCDate(value.getUTCDate() + days);
  return value.toISOString().slice(0, 10);
}

/**
 * Ряд графика по дням периода: пропущенные сервером дни — нули, будущие дни
 * не рисуем, сегодняшний — «период ещё не закончился». null — Go ещё не
 * отдаёт byDay (страница показывает пустое состояние, а не нули).
 */
export function daySeries(
  byDay: SpendDay[] | undefined | null,
  from: string,
  to: string,
  today: string,
): SpendPoint[] | null {
  if (!Array.isArray(byDay)) return null;
  // Ключ — первые 10 знаков: если сервер пришлёт дату со временем, день не потеряется.
  const map = new Map(byDay.map((row) => [String(row.date).slice(0, 10), row]));
  const last = to < today ? to : today;
  const points: SpendPoint[] = [];
  if (!from || !last || from > last) return points;
  for (
    let day = from;
    day <= last && points.length < 1100;
    day = shift(day, 1)
  ) {
    const row = map.get(day);
    const spent = row?.spent ?? "0.00";
    const value = Number(spent);
    points.push({
      date: day,
      label: `${day.slice(8, 10)}.${day.slice(5, 7)}`,
      value: Number.isFinite(value) ? value : 0,
      partial: day === today,
      spent,
      messages: row?.messages ?? 0,
    });
  }
  return points;
}

/**
 * Ряд сравнения той же длины, что основной: i-й день сравнения против i-го
 * дня периода. null — сравнения нет или Go не прислал byDay.
 */
export function compareSeries(
  points: SpendPoint[],
  byDay: SpendDay[] | undefined | null,
  compareFrom: string | undefined,
): (number | null)[] | null {
  if (!Array.isArray(byDay) || !compareFrom) return null;
  const map = new Map(byDay.map((row) => [String(row.date).slice(0, 10), row]));
  return points.map((_, index) => {
    const value = Number(map.get(shift(compareFrom, index))?.spent ?? "0");
    return Number.isFinite(value) ? value : null;
  });
}

/** Подписи каналов простым языком. */
export const CHANNEL_LABEL: Record<string, string> = {
  sms: "SMS",
  email: "Электронная почта",
  push: "Пуши приложения",
  telegram: "Telegram",
};

/** Строка канала; undefined — сервер не прислал разбивку (Go ещё не обновлён). */
export function channelRow(
  byChannel: SpendChannel[] | undefined | null,
  channel: string,
): SpendChannel | undefined {
  if (!Array.isArray(byChannel)) return undefined;
  return (
    byChannel.find((row) => row.channel === channel) ?? {
      channel,
      spent: "0.00",
      messages: 0,
    }
  );
}

/** Каналы для разреза «По каналам»: сначала SMS, почта, пуши, затем остальные. */
export function channelRows(
  byChannel: SpendChannel[] | undefined | null,
): Array<SpendChannel & { label: string; share: number }> | null {
  if (!Array.isArray(byChannel)) return null;
  const order = ["sms", "email", "push"];
  const rows = [
    ...order.map((channel) => channelRow(byChannel, channel)!),
    ...byChannel.filter((row) => !order.includes(row.channel)),
  ];
  const value = (row: SpendChannel) => {
    const n = Number(row.spent);
    return Number.isFinite(n) && n > 0 ? n : 0;
  };
  const max = Math.max(0, ...rows.map(value));
  return rows.map((row) => ({
    ...row,
    label: CHANNEL_LABEL[row.channel] ?? row.channel,
    share: max > 0 ? Math.round((value(row) / max) * 1000) / 10 : 0,
  }));
}

export interface LimitRow {
  id: string;
  name: string;
  companyId: string;
  status: string;
  spent: string;
  /** Лимит бюджета; null — кампания без лимита (расход ограничен балансом). */
  budget: string | null;
  /** Потрачено от лимита, % (0..100) — для полосы и подписи; без лимита — 0. */
  percent: number;
  tone: "success" | "warning" | "error";
  /** Пауза между контактами с одним гостем, дней. */
  frequencyDays?: number;
  /** Через сколько дней гость может снова попасть в кампанию, дней. */
  reentryDays?: number;
}

const CURRENT: readonly string[] =
  CAMPAIGN_STATUS_GROUPS.find((g) => g.value === "current")?.statuses ?? [];

/**
 * «Ограничения кампаний»: все действующие кампании (работает / на паузе) с их
 * ограничениями — лимит бюджета (сколько потрачено из лимита), пауза между
 * контактами и повторное участие. Кампании без лимита тоже в списке: их расход
 * ограничен только балансом. Сначала с лимитом (по доле потраченного), затем
 * без лимита по названию. Больше 85 % — красный, больше 60 % — жёлтый.
 */
export function limitRows(
  campaigns: Array<
    Pick<
      MarketingCampaign,
      | "id"
      | "name"
      | "companyId"
      | "status"
      | "budget"
      | "unlimitedBudget"
      | "spent"
    > &
      Partial<Pick<MarketingCampaign, "frequencyDays" | "reentryDays">>
  >,
): LimitRow[] {
  const rows: LimitRow[] = [];
  for (const c of campaigns) {
    if (!CURRENT.includes(c.status)) continue;
    const limit = toCents(c.budget);
    const budget =
      !c.unlimitedBudget && limit !== null && limit > 0n ? limit : null;
    const spent = toCents(c.spent) ?? 0n;
    const percent =
      budget === null
        ? 0
        : Math.max(0, Math.min(100, Number((spent * 1000n) / budget) / 10));
    rows.push({
      id: c.id,
      name: c.name,
      companyId: c.companyId,
      status: c.status,
      spent: centsToMoney(spent),
      budget: budget === null ? null : centsToMoney(budget),
      percent,
      tone: percent > 85 ? "error" : percent > 60 ? "warning" : "success",
      frequencyDays: c.frequencyDays,
      reentryDays: c.reentryDays,
    });
  }
  return rows.sort((a, b) => {
    if ((a.budget === null) !== (b.budget === null))
      return a.budget === null ? 1 : -1;
    if (a.budget !== null) return b.percent - a.percent;
    return a.name.localeCompare(b.name, "ru");
  });
}

/** Подписи операций журнала простым языком и тон бейджа. */
export const OP_KIND: Record<string, { label: string; tone: StatusTone }> = {
  topup: { label: "Пополнение", tone: "success" },
  adjust: { label: "Корректировка", tone: "info" },
  charge: { label: "Списание за сообщение", tone: "neutral" },
  reserve: { label: "Резерв под кампанию", tone: "warning" },
  // Свёрнутая строка ленты (view=summary): все списания кампании за день МСК.
  messages: { label: "Списания за сообщения", tone: "neutral" },
  release: { label: "Возврат резерва", tone: "neutral" },
  refund: { label: "Возврат", tone: "success" },
};

/**
 * Ответ /api/billing/ops: лента без accountId (УК — все счета, партнёр с
 * несколькими юрлицами — свои; 50 последних) — `items`, журнал счёта — `ops`.
 * Go отдаёт оба ключа с одним списком.
 */
export function opsItems(
  response: { items?: FinanceOp[]; ops?: FinanceOp[] } | undefined,
): FinanceOp[] {
  return response?.items ?? response?.ops ?? [];
}

/**
 * Ключ строки журнала — id операции из Go. Запасной ключ (старый сервер без id)
 * с номером строки: одинаковые операции одной транзакции не должны совпасть.
 */
export function opRowKey(row: FinanceOp, index: number): string {
  return row.id
    ? `op-${row.id}`
    : `row-${index}-${row.accountId ?? ""}-${row.at}-${row.kind}-${row.amount}`;
}

/** Знак суммы операции для цвета: плюс — зачисление. */
export function moneySign(amount: string): 1 | 0 | -1 {
  const cents = toCents(amount);
  if (cents === null || cents === 0n) return 0;
  return cents > 0n ? 1 : -1;
}

/** «+1 000,00 ₽» для зачислений, как в прототипе. */
export function signedMoney(
  amount: string,
  format: (value: string) => string,
): string {
  return (moneySign(amount) > 0 ? "+" : "") + format(amount);
}

/**
 * Хватит примерно на N частей SMS: сумма и цена части — строки ₽, делим в
 * копейках. Цена — partnerSmsPrice из /api/estimate (как реально спишет Go, с
 * наценкой УК), а не ручная оценка УК. Нет цены или суммы — null: не показываем.
 */
export function smsCapacity(
  amount: string | undefined,
  partnerSmsPrice: string | null | undefined,
): number | null {
  const value = toCents(amount);
  const price = toCents(partnerSmsPrice);
  if (value === null || value < 0n || price === null || price <= 0n)
    return null;
  return Number(value / price);
}

/**
 * Хватит примерно на N дней при среднем расходе за days дней (spent — spent30d
 * счёта из /api/billing); null — расхода не было или сервер его не прислал.
 */
export function daysCapacity(
  amount: string | undefined,
  spent: string | undefined,
  days = 30,
): number | null {
  const value = Number(amount);
  const perDay = Number(spent) / days;
  if (
    amount === undefined ||
    !Number.isFinite(value) ||
    value < 0 ||
    !Number.isFinite(perDay) ||
    perDay <= 0
  )
    return null;
  return Math.floor(value / perDay);
}

/** Средняя цена сообщения в копейках с округлением: total / messages. */
export function averagePrice(
  total: string | undefined,
  messages: number | undefined,
): string | undefined {
  const cents = toCents(total);
  if (cents === null || !messages || messages <= 0) return undefined;
  const count = BigInt(Math.floor(messages));
  return centsToMoney((cents * 2n + count) / (count * 2n));
}

/** Русское множественное: plural(5, 'день', 'дня', 'дней'). */
export function plural(
  n: number,
  one: string,
  few: string,
  many: string,
): string {
  const m10 = Math.abs(n) % 10;
  const m100 = Math.abs(n) % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}

/** «1 234» — количество с разрядами; нет значения — 0. */
export function formatCount(value: number | undefined | null): string {
  return (value ?? 0).toLocaleString("ru-RU");
}
